import { useState } from "react";
import Requester from "../helpers/Requester";
import App from "../layouts/app";
import clsx from "clsx";
import { Link } from "@inertiajs/react";
import { Trash } from "lucide-react";

enum statusColor {
    'Awaiting' = 'bg-gray-100',
    'Ready' = 'bg-blue-100',
    'Paid' = 'bg-green-100',
    'Error' = 'bg-red-100',
    'Sorted' = 'bg-purple-100',
}
enum hoverColor {
    'Awaiting' = 'hover:bg-gray-50',
    'Ready' = 'hover:bg-blue-50',
    'Paid' = 'hover:bg-green-50',
    'Error' = 'hover:bg-red-50',
    'Sorted' = 'hover:bg-purple-50',
}

export enum Statuses {
    'Awaiting' = 1,
    'Ready' = 2,
    'Paid' = 3,
    'Error' = 4,
    'Sorted' = 5,
}

function statusToColor(status: number) {
    return statusColor[Statuses[status] as keyof typeof Statuses];
}
function statusToHoverColor(status: number) {
    return hoverColor[Statuses[status] as keyof typeof Statuses];
}


export default function OnlinePayments({ applications: initialApplications }) {
    const [applications, setApplications] = useState(initialApplications);

    const updateComment = (id: number, comment: string) => {
        const requester = new Requester();

        requester.post(requester.getUrl(`api/applications/${id}/comment`), { comment });
    }

    const updateStatus = (id: number, status: string) => {
        const requester = new Requester();

        requester.post(requester.getUrl(`api/applications/${id}/status`), { status }).then(() => {
            for (let i = 0; i < applications.length; i++) {
                if (applications[i].id === id) {
                    applications[i].status = parseInt(status);
                }
            }

            setApplications([...applications]);
        });
    }

    const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);

    const sortData = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';

        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }

        setSortConfig({ key, direction });

        const sortedApps = [...applications].sort((a, b) => {
            const valueA = a[key] ?? '';
            const valueB = b[key] ?? '';

            // DATE
            if (key === 'created_at') {
                return direction === 'asc'
                    ? new Date(valueA).getTime() - new Date(valueB).getTime()
                    : new Date(valueB).getTime() - new Date(valueA).getTime();
            }

            // NUMBER
            if (!isNaN(valueA) && !isNaN(valueB)) {
                return direction === 'asc'
                    ? Number(valueA) - Number(valueB)
                    : Number(valueB) - Number(valueA);
            }

            // STRING
            return direction === 'asc'
                ? String(valueA).localeCompare(String(valueB))
                : String(valueB).localeCompare(String(valueA));
        });

        setApplications(sortedApps);
    };

    const getSortIcon = (key: string) => {
        if (!sortConfig || sortConfig.key !== key) return "⇅";
        return sortConfig.direction === 'asc' ? "↑" : "↓";
    };

    return (
        <App>
            <div className="w-full">
                <div className="max-w-dasha mx-auto py-20 pt-0 flex flex-col space-y-7">
                    <div className="flex items-center justify-between">
                        <h1 className="text-3xl font-medium">Online Payments</h1>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 border rounded-lg overflow-hidden shadow-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th onClick={() => sortData('id')} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer">
                                        ID <span className="text-xs opacity-60">{getSortIcon('id')}</span>
                                    </th>

                                    <th onClick={() => sortData('competition_name')} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer">
                                        Competition <span className="text-xs opacity-60">{getSortIcon('competition_name')}</span>
                                    </th>

                                    <th onClick={() => sortData('gymnast')} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer">
                                        Gymnast <span className="text-xs opacity-60">{getSortIcon('gymnast')}</span>
                                    </th>

                                    <th onClick={() => sortData('email')} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer">
                                        Email <span className="text-xs opacity-60">{getSortIcon('email')}</span>
                                    </th>

                                    <th onClick={() => sortData('customer_name')} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer">
                                        Customer <span className="text-xs opacity-60">{getSortIcon('customer_name')}</span>
                                    </th>

                                    <th onClick={() => sortData('phone')} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer">
                                        Phone <span className="text-xs opacity-60">{getSortIcon('phone')}</span>
                                    </th>

                                    <th onClick={() => sortData('payment_method')} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer">
                                        Payment <span className="text-xs opacity-60">{getSortIcon('payment_method')}</span>
                                    </th>

                                    <th onClick={() => sortData('created_at')} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer">
                                        Date <span className="text-xs opacity-60">{getSortIcon('created_at')}</span>
                                    </th>
                                    <th onClick={() => sortData('comment')} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer">
                                        Comment <span className="text-xs opacity-60">{getSortIcon('comment')}</span>
                                    </th>
                                    <td></td>

                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-200">
                                {applications?.map((application) => (
                                    <tr key={application.id} className={clsx("transition", statusToHoverColor(application.status), statusToColor(application.status))}>
                                        <td className="px-4 py-3 text-sm">{application.id}</td>
                                        <td className="px-4 py-3 text-sm">{application.competition_name}</td>
                                        <td className="px-4 py-3 text-sm">{application.gymnast}</td>
                                        <td className="px-4 py-3 text-sm">{application.email}</td>
                                        <td className="px-4 py-3 text-sm">{application.customer_name}</td>
                                        <td className="px-4 py-3 text-sm">{application.phone}</td>
                                        <td className="px-4 py-3 text-sm">
                                            {application.payment_method}
                                            {application.payment_method === 'online' ? ` (${application.payment_status})` : null}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{new Date(application.created_at).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <input
                                                className="w-full min-w-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                placeholder="Comment"
                                                defaultValue={application.comment}
                                                onChange={(e) => updateComment(application.id, e.target.value)}
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <select defaultValue={application.status} onChange={(e) => updateStatus(application.id, e.target.value)} className="w-full min-w-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                                <option value="1">Ожидает</option>
                                                <option value="2">Готово</option>
                                                <option value="3">Оплачено</option>
                                                <option value="4">Ошибка</option>
                                                <option value="5">Отсортировано</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div >
            </div >
        </App >
    )
}